import React, {FC, useMemo} from "react";
import useTranslate from "../../core/languages/hooks/useTranslate";
import useCustomerContext from "../../customers/hooks/useCustomerContext";
import Transfer from "../../transfers/types/Transfer";
import moment from "moment";
import {TransferDirectionEnum} from "../../transfers/enums/TransferDirectionEnum";
import TransportLocation from "../../transfers/types/TransportLocation";
import {TransferCommonDescription} from "../../transfers/types/TransferCommonDescription";
import TransferRoute from "../../transfers/types/TransferRoute";

export interface TransferItemCardProps {
  transfer: Transfer;
  locationFrom?: TransportLocation;
  locationTo?: TransportLocation;
  parentTransfer?: TransferCommonDescription;
  route?: TransferRoute;
}

const TransferItemCard: FC<TransferItemCardProps> = ({transfer, locationFrom, locationTo, parentTransfer, route}) => {
  const t = useTranslate();
  const {languageCode} = useCustomerContext();

  // Get transfer direction label
  const directionLabel = useMemo(() => {
    switch (transfer.direction) {
      case TransferDirectionEnum.ARRIVAL:
        return t('common.enum.transferDirection.oneWay') || 'Arrival';
      case TransferDirectionEnum.DEPARTURE:
        return t('common.enum.transferDirection.comeBack') || 'Departure';
      case TransferDirectionEnum.ROUND_TRIP:
        return t('common.enum.transferDirection.both') || 'Round Trip';
      default:
        return 'Transfer';
    }
  }, [transfer.direction, t]);

  return (
    <div className="w-full">
      <div className="space-y-5">
        {/* Main Info Row */}
        <div className="flex items-center space-x-3 bg-neutral-50 dark:bg-neutral-800 px-3 py-2 rounded-lg">
          <div className="flex-shrink-0">
            {transfer.direction === TransferDirectionEnum.ARRIVAL ?
              <i className="las la-plane-arrival text-amber-500 dark:text-amber-400 text-2xl"></i> :
              <i className="las la-plane-departure text-amber-500 dark:text-amber-400 text-2xl"></i>
            }
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
              {t("transfers.fields.direction")}
            </p>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
              {directionLabel}
            </p>
          </div>
        </div>

        <div className="border-t border-neutral-200 dark:border-neutral-700"></div>

        {/* Location & Time Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3 bg-neutral-50 dark:bg-neutral-800 px-3 py-2 rounded-lg">
            <div className="flex-shrink-0">
              <i className="las la-map-pin text-amber-500 dark:text-amber-400 text-2xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                {t("transferOk.fields.locationFrom")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
                {locationFrom?.trName[languageCode] || '-'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-neutral-50 dark:bg-neutral-800 px-3 py-2 rounded-lg">
            <div className="flex-shrink-0">
              <i className="las la-map-pin text-amber-500 dark:text-amber-400 text-2xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                {t("transferOk.fields.locationTo")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
                {locationTo?.trName[languageCode] || '-'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-neutral-50 dark:bg-neutral-800 px-3 py-2 rounded-lg">
            <div className="flex-shrink-0">
              <i className="las la-users text-amber-500 dark:text-amber-400 text-2xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                {t("transferOk.fields.numberOfPassengers")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
                {transfer.numberOfPassengers || 0} {transfer.numberOfPassengers === 1 ? t("transferOk.fields.person") : t("transferOk.fields.persons")}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 bg-neutral-50 dark:bg-neutral-800 px-3 py-2 rounded-lg">
            <div className="flex-shrink-0">
              <i className="las la-clock text-amber-500 dark:text-amber-400 text-2xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                {t("transfers.fields.pickUpTime")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mt-1">
                {transfer.pickUpTime ? (typeof transfer.pickUpTime === 'string' ? transfer.pickUpTime : moment(transfer.pickUpTime).format('HH:mm')) : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Shaamber Passengers */}
        {parentTransfer && parentTransfer.passengersSharing && (
          <>
            <div className="border-t border-neutral-200 dark:border-neutral-500"></div>
            <div>
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-3">
                {t("transferOk.message.sharedWithPassenger")}
              </p>
              <div className="space-y-2">
                {parentTransfer.passengersSharing
                  .filter(passenger => !(passenger.id === transfer.uuid))
                  .map((passenger, index) => (
                    <div key={index} className="flex items-center space-x-3 bg-neutral-50 dark:bg-neutral-800 px-3 py-2 rounded-lg">
                      <div className="flex-shrink-0">
                        <i className="las la-user-circle text-amber-500 dark:text-amber-400 text-2xl"></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                            {passenger.passengerName} {passenger.passengerLastName}
                        </p>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          </>
        )}

        {/* Transfer Instructions */}
        {route && (
          <>
            <div className="border-t border-neutral-200 dark:border-neutral-700"></div>
            <div className="space-y-3">
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                        {transfer.direction === TransferDirectionEnum.ARRIVAL ?
                          t("transferOk.fields.arrivalInstructions") : t("transferOk.fields.departureInstructions")
                        }
                    </span>
                </div>
                <div
                  className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-900 dark:text-amber-100">
                    {transfer.direction === TransferDirectionEnum.ARRIVAL ?
                      route.trArrivalInstructions[languageCode] : route.trDepartureInstructions[languageCode]
                    }
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TransferItemCard;
